import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { ExtractionBadge, VersionStateBadge, fmtBytes, fmtDateTime } from "./PolicyBadges";

export const downloadVersion = async (policyId, v) => {
  try {
    const res = await api.get(`/policies/${policyId}/file`, { params: { version_id: v.id }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = v.file_name || `policy.${v.file_type}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Download not permitted");
  }
};

export const PolicyVersions = ({ policyId, versions, selectedId, onSelect, can, onChanged, policyStatus }) => {
  const fileRef = useRef(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", file); fd.append("version_label", label);
    setBusy(true);
    try {
      const { data } = await api.post(`/policies/${policyId}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.status === "Pending Review" ? "New version uploaded — approval required before readers see it" : "New version uploaded");
      setLabel(""); if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="nv-card p-5 space-y-4" data-testid="policy-versions">
      <h3 className="text-h3">Versions</h3>
      <ul className="divide-y divide-slate-100">
        {versions.map((v) => (
          <li key={v.id} className={`py-3 flex flex-wrap items-center gap-3 ${selectedId === v.id ? "bg-sky-50/60 -mx-2 px-2 rounded" : ""}`} data-testid={`version-row-${v.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">{v.version_label}</span>
                <VersionStateBadge state={v.state} />
                <ExtractionBadge status={v.extraction_status} corrected={v.text_corrected} />
              </div>
              <p className="text-xs text-slate-500 mt-1">{v.file_name} · {fmtBytes(v.size_bytes)} · {v.uploaded_by_name} · {fmtDateTime(v.uploaded_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onSelect(v)} className="text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`version-preview-${v.id}`}><Eye size={13} /> Preview</button>
              {can.download && <button onClick={() => downloadVersion(policyId, v)} className="text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`version-download-${v.id}`}><Download size={13} /> Download</button>}
            </div>
          </li>
        ))}
      </ul>
      {can.upload_version && policyStatus !== "Archived" && (
        <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="version-upload-form">
          <label className="block text-xs font-semibold text-slate-600">Replace with a new version (PDF / .docx)
            <input ref={fileRef} type="file" accept=".pdf,.docx" className="mt-1.5 w-full text-sm" data-testid="version-file-input" /></label>
          <div className="flex gap-2 items-end">
            <label className="flex-1 text-xs font-semibold text-slate-600">Label
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="auto" className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 text-sm" data-testid="version-label-input" /></label>
            <button onClick={upload} disabled={busy} className="px-3 py-2 text-sm rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="version-upload-submit"><Upload size={14} /> {busy ? "Uploading…" : "Upload version"}</button>
          </div>
          {policyStatus === "Active" && <p className="text-[12.5px] text-amber-700">The current approved version stays visible to readers until the new one is approved.</p>}
        </div>
      )}
    </div>
  );
};
